import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../components/ui";
import { markdownToPlain } from "../components/RichText";
import type { Discussion, LexData } from "../lib/types";

type Filter = "all" | "questions" | "proposals" | "knowledge" | "resources" | "following";

const filters: { value: Filter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "questions", label: "Questions" },
  { value: "proposals", label: "Proposals & votes" },
  { value: "knowledge", label: "Notes & memoranda" },
  { value: "resources", label: "Resources" },
  { value: "following", label: "Following" },
];

const knowledgeTypes = new Set<Discussion["type"]>(["practice_note", "memorandum", "joint_position", "legislative_comment"]);

export function DiscussionsPage({ data }: { data: LexData; mode?: "convex" | "demo" }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");

  const followedIds = useMemo(() => followedDiscussionIds(data), [data]);

  const threads = useMemo(() => {
    const query = search.trim().toLowerCase();
    const sorted = data.discussions.slice().sort((a, b) => b.lastActivityAt - a.lastActivityAt);
    return sorted.filter((discussion) => {
      const matchesFilter =
        filter === "questions"
          ? discussion.postType === "Question"
          : filter === "proposals"
            ? discussion.postType === "Proposal" || Boolean(discussion.linkedGovernanceProposalId)
            : filter === "knowledge"
              ? knowledgeTypes.has(discussion.type) || discussion.postType === "Resource" || discussion.postType === "Update"
              : filter === "resources"
                ? discussion.postType === "Resource"
                : filter === "following"
                  ? followedIds.has(discussion.id)
                  : true;
      if (!matchesFilter) return false;
      if (!query) return true;
      return `${discussion.title} ${discussion.body} ${(discussion.topicTags ?? []).join(" ")} ${discussion.jurisdictionTags.join(" ")}`.toLowerCase().includes(query);
    });
  }, [data.discussions, filter, followedIds, search]);

  return (
    <>
      <div className="phead">
        <div className="eyebrow">Network projects</div>
        <h1 className="h1 serif">Discussions &amp; proposals</h1>
        <p className="lead">Questions, resources, governance proposals and votes the network is working through. Filter to what touches your practice or your firm.</p>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {filters.map((item) => (
          <button key={item.value} type="button" className={`fchip${filter === item.value ? " fchip-on" : ""}`} onClick={() => setFilter(item.value)}>
            {item.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 18 }}>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search the knowledge base — titles, notes, topics, jurisdictions"
          className="w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-sm"
          aria-label="Search discussions and memoranda"
        />
      </div>

      <div className="card">
        <div className="cardhd">
          <span className="cardtitle">{threads.length} active {threads.length === 1 ? "thread" : "threads"}</span>
          <span className="row-meta" style={{ margin: 0 }}>Most recent activity first</span>
        </div>
        {threads.length > 0 ? (
          threads.map((discussion) => <ThreadRow key={discussion.id} discussion={discussion} data={data} />)
        ) : (
          <div className="rec"><div className="row-meta" style={{ margin: 0 }}>No threads match this filter.</div></div>
        )}
      </div>
    </>
  );
}

function ThreadRow({ discussion, data }: { discussion: Discussion; data: LexData }) {
  const author = data.users.find((user) => user.id === discussion.createdByUserId);
  const firm = data.firms.find((item) => item.id === discussion.createdByFirmId);
  const isMine = discussion.createdByUserId === data.viewer.id;
  const contributors = new Set([discussion.createdByUserId, ...data.contributions.filter((item) => item.discussionId === discussion.id).map((item) => item.createdByUserId)]).size;
  const replies = data.contributions.filter((item) => item.discussionId === discussion.id).length;
  const action = threadAction(discussion, isMine);
  const authorUser = isMine ? data.viewer : author;
  const authorLabel = isMine ? `${data.viewer.name} · you` : `${author?.name ?? "Member"} · ${firm?.name ?? "Member firm"}`;

  return (
    <div className="rec">
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 7 }}>
        <span className={`pill ${pillForStatus(discussion.workflowStatus)}`}>{discussion.workflowStatus ?? discussion.status}</span>
        <span className="tag tag-accent">{discussion.postType ?? "Discussion"}</span>
        <span className="row-meta" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 7 }}>
          <Avatar name={authorUser?.name ?? "Member"} imageUrl={authorUser?.avatarUrl} size="sm" />
          {authorLabel} · {relativeAge(discussion.lastActivityAt)}
        </span>
      </div>
      <div className="row-title">{discussion.title}</div>
      <p className="lead" style={{ fontSize: 16, marginTop: 5, maxWidth: "none" }}>{bodySnippet(discussion.body)}</p>
      <div className="recmeta">
        {discussion.confidentialityLevel === "Full Members only" ? <span>Full Members only</span> : replies > 0 ? <span>{replies} {replies === 1 ? "reply" : "replies"}</span> : <span>{contributors} {contributors === 1 ? "contributor" : "contributors"}</span>}
        <span>{deadlineLabel(discussion)}</span>
        <Link className={`btn-sm${action.primary ? " btn-primary" : ""}`} style={{ marginLeft: "auto", height: 30 }} to={action.href}>{action.label}</Link>
      </div>
    </div>
  );
}

function threadAction(discussion: Discussion, isMine: boolean): { label: string; href: string; primary: boolean } {
  if (discussion.linkedGovernanceProposalId && discussion.workflowStatus === "Voting open") {
    return { label: "Vote now", href: `/discussions/proposals/${discussion.linkedGovernanceProposalId}`, primary: true };
  }
  if (discussion.status === "drafting" && isMine) {
    return { label: "Continue", href: `/discussions?compose=1&draft=${discussion.id}`, primary: true };
  }
  if (discussion.postType === "Question") {
    return { label: "Reply", href: `/discussions?discussion=${discussion.id}`, primary: false };
  }
  return { label: "Open", href: `/discussions?discussion=${discussion.id}`, primary: false };
}

function pillForStatus(status?: string) {
  switch (status) {
    case "Voting open":
      return "pill-warn";
    case "Input requested":
      return "pill-sky";
    default:
      return "pill-neutral";
  }
}

function deadlineLabel(discussion: Discussion) {
  if (discussion.status === "drafting") return "Private draft";
  if (!discussion.deadline) return "No deadline";
  const days = Math.round((discussion.deadline - Date.now()) / (24 * 60 * 60 * 1000));
  if (discussion.workflowStatus === "Voting open") return days <= 0 ? "Closing today" : `Closes in ${days} days`;
  return days <= 0 ? "Due today" : `Due in ${days} days`;
}

function followedDiscussionIds(data: LexData) {
  const ids = new Set<string>();
  for (const saved of data.savedItems.filter((item) => item.userId === data.viewer.id)) {
    if (saved.itemType === "discussion") ids.add(saved.itemId);
    if (saved.itemType === "governanceProposal") {
      const proposal = data.proposals.find((item) => item.id === saved.itemId);
      if (proposal) ids.add(proposal.discussionId);
    }
  }
  return ids;
}

function relativeAge(time: number) {
  const abs = Math.abs(time - Date.now());
  const hour = 60 * 60 * 1000;
  if (abs < hour) return "just now";
  if (abs < 24 * hour) return `${Math.max(1, Math.round(abs / hour))}h ago`;
  return `${Math.max(1, Math.round(abs / (24 * hour)))}d ago`;
}

// One-line snippet for the list: first paragraph of the body, markdown stripped and capped.
function bodySnippet(body: string) {
  const plain = markdownToPlain(body.split(/\n\s*\n/)[0]?.trim() || body);
  return plain.length > 180 ? `${plain.slice(0, 180).trimEnd()}…` : plain;
}
